<template>
  <div>
    <v-toolbar flat color="white">
      <v-toolbar-title>Mutabakat</v-toolbar-title>
      <v-divider class="mx-3" inset vertical />
      <v-spacer />
      <v-btn icon @click="refresh" :loading="loadingData">
        <v-icon>refresh</v-icon>
      </v-btn>
    </v-toolbar>

    <v-alert
      v-if="!selectedTenantId || !selectedMpId"
      type="warning"
      dismissible
      class="ma-2"
    >
      Lütfen header'dan (üst kısımdan) Ürün ve MarketPlace seçin.
    </v-alert>

    <v-alert
      v-if="selectedTenantId && selectedMpId && (!reconciliationSummary || reconciliationSummary.length === 0) && !loadingData"
      type="info"
      class="ma-2"
    >
      <strong>Mutabakat Nasıl Çalışır?</strong>
      <p class="mt-2 mb-0">
        Mutabakat verisi <strong>otomatik olarak</strong> hesaplanır ve gösterilir.<br>
        Beklenen ödemeler sipariş sync anında hesaplanır, gelen ödemelerle canlı karşılaştırma yapılır.<br>
        Tarih aralığı seçip <strong>"Filtrele"</strong> butonuna basarak sonuçları görüntüleyebilirsiniz.
      </p>
      <p class="mt-2 mb-0">
        <router-link to="/admin/expected-payments">Admin panelinden</router-link> komisyon oranları, kargo bedeli, stopaj oranları gibi kuralları tanımlayabilirsiniz.
      </p>
    </v-alert>

    <!-- Filtre Alanı -->
    <v-card class="ma-2 pa-3" v-if="selectedTenantId && selectedMpId">
      <v-card-title class="pa-0 pb-2">
        <span class="subtitle-1">Filtre Alanı</span>
        <v-spacer />
        <v-chip small color="info" text-color="white" v-if="loadingData">
          <v-progress-circular size="12" width="2" indeterminate class="mr-1" />
          Yükleniyor...
        </v-chip>
      </v-card-title>
      <v-layout wrap>
        <v-flex xs12 md3 class="px-1">
          <v-menu
            v-model="orderStartDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="orderStartDate"
                label="Sipariş Baş. Tarihi"
                prepend-icon="event"
                readonly
                v-on="on"
                clearable
                @click:clear="onDateClear"
              />
            </template>
            <v-date-picker
              v-model="orderStartDate"
              locale="tr-TR"
              @input="onDateChange"
            />
          </v-menu>
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-menu
            v-model="orderEndDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="orderEndDate"
                label="Sipariş Bit. Tarihi"
                prepend-icon="event"
                readonly
                v-on="on"
                clearable
                @click:clear="onDateClear"
              />
            </template>
            <v-date-picker
              v-model="orderEndDate"
              locale="tr-TR"
              :min="orderStartDate"
              @input="onDateChange"
            />
          </v-menu>
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-text-field
            v-model="orderNumberFilter"
            label="Sipariş Numarası"
            prepend-icon="search"
            clearable
            @keyup.enter="applyFilter"
          />
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-select
            v-model="orderStatus"
            :items="orderStatusOptions"
            item-text="text"
            item-value="value"
            label="Sipariş Durumu"
            clearable
          />
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-select
            v-model="reconciliationStatus"
            :items="reconciliationStatusOptions"
            item-text="text"
            item-value="value"
            label="Mutabakat Durumu"
            clearable
          />
        </v-flex>
        <v-flex xs12 md3 class="px-1">
          <v-menu
            v-model="deliveredDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="deliveredDate"
                label="Teslim Tarihi"
                prepend-icon="local_shipping"
                readonly
                v-on="on"
                clearable
                hint="Sadece bu tarihte teslim edilmiş siparişler"
                persistent-hint
              />
            </template>
            <v-date-picker
              v-model="deliveredDate"
              locale="tr-TR"
              @input="deliveredDateMenu = false"
            />
          </v-menu>
        </v-flex>
        <v-flex xs12 md6 class="px-1 d-flex align-center">
          <v-btn color="primary" @click="applyFilter" class="mr-2" :loading="loadingData">
            <v-icon left>search</v-icon>
            Filtrele
          </v-btn>
          <v-btn @click="clearFilters">
            <v-icon left>clear</v-icon>
            Temizle
          </v-btn>
          <v-btn 
            color="warning" 
            dark 
            @click="handleRecalculateAll" 
            :loading="recalculating"
            class="ml-2"
          >
            <v-icon left>refresh</v-icon>
            Tümünü Yeniden Hesapla
          </v-btn>
          <v-btn 
            color="error" 
            dark 
            @click="handleRefreshPayments" 
            :loading="refreshingPayments"
            class="ml-2"
            v-if="selectedMpId === 1"
          >
            <v-icon left>sync</v-icon>
            Ödemeleri Yenile
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Özet Tablosu -->
    <v-card class="ma-2" v-if="selectedTenantId && selectedMpId">
      <v-card-title class="pb-0">
        <span class="subtitle-1 font-weight-bold">Özet</span>
      </v-card-title>
    <v-data-table
      :headers="summaryHeaders"
      :items="reconciliationSummary"
      :loading="$store.state.progress"
        class="elevation-0"
        hide-actions
    >
      <template v-slot:items="props">
          <tr 
            :class="{ 'clickable-row': true }" 
            @click="viewDetails(props.item.transactionType)"
          >
        <td>{{ getTransactionTypeName(props.item.transactionType) }}</td>
            <td class="text-xs-right">{{ props.item.totalOrders }}</td>
            <td class="text-xs-right">{{ formatMoney(props.item.actualTotal) }}</td>
            <td class="text-xs-right">{{ formatMoney(props.item.expectedTotal) }}</td>
            <td class="text-xs-right" :class="getDifferenceClass(props.item.difference)">
              {{ formatMoney(props.item.difference) }}
            </td>
            <td class="text-xs-center">
              <v-chip 
                :color="getReconciliationStatusColor(props.item)" 
                text-color="white" 
                small
              >
                {{ getReconciliationStatusText(props.item) }}
              </v-chip>
            </td>
          </tr>
        </template>
        <template v-slot:footer v-if="reconciliationSummary && reconciliationSummary.length > 0">
          <tr class="grey lighten-3 font-weight-bold">
            <td>Toplam</td>
            <td class="text-xs-right">{{ getTotalOrders }}</td>
            <td class="text-xs-right">{{ formatMoney(getTotalActual) }}</td>
            <td class="text-xs-right">{{ formatMoney(getTotalExpected) }}</td>
            <td class="text-xs-right" :class="getDifferenceClass(getTotalDifference)">
              {{ formatMoney(getTotalDifference) }}
            </td>
            <td class="text-xs-center">
              <v-chip 
                :color="getTotalDifference === 0 ? 'green' : 'red'" 
                text-color="white" 
                small
              >
                {{ Math.abs(getTotalDifference) < 0.01 ? 'Mutabık' : 'Mutabık değil' }}
              </v-chip>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="text-xs-center pa-4">
            <p class="grey--text">Bu tarih aralığında mutabakat verisi bulunamadı</p>
            <p class="grey--text text--lighten-1">Farklı bir tarih aralığı seçip "Filtrele" butonuna basın</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Detay Tablosu - Sipariş Bazında -->
    <v-card class="ma-2" v-if="selectedTenantId && selectedMpId && reconciliationSummary && reconciliationSummary.length > 0">
      <v-card-title class="pb-0">
        <span class="subtitle-1 font-weight-bold">Detay</span>
        <v-spacer />
        <v-select
          v-model="detailFilterStatus"
          :items="detailStatusOptions"
          item-text="text"
          item-value="value"
          label="Durum Filtrele"
          style="max-width: 200px; margin-right: 12px;"
          hide-details
        />
        <v-btn color="primary" small @click="exportToExcel">
          <v-icon left small>file_download</v-icon>
          Excel'e Aktar
        </v-btn>
      </v-card-title>
      <v-data-table
        :headers="detailHeaders"
        :items="filteredOrderDetails"
        :loading="loadingDetails"
        :total-items="orderDetailsTotalCount"
        :pagination.sync="detailPagination"
        :rows-per-page-items="[25, 50, 100]"
        class="elevation-0"
      >
        <template v-slot:items="props">
          <tr 
            :class="{ 'clickable-row': true }" 
            @click="showOrderBreakdown(props.item)"
          >
            <td>{{ props.item.orderNumber }}</td>
            <td>{{ props.item.sku || '-' }}</td>
        <td class="text-xs-right">{{ formatMoney(props.item.expectedTotal) }}</td>
        <td class="text-xs-right">{{ formatMoney(props.item.actualTotal) }}</td>
            <td class="text-xs-right" :class="getDifferenceClass(props.item.difference)">
          {{ formatMoney(props.item.difference) }}
        </td>
            <td class="text-xs-center">
              <v-chip 
                :color="Math.abs(props.item.difference) < 0.01 ? 'green' : 'red'" 
                text-color="white" 
                small
              >
                {{ Math.abs(props.item.difference) < 0.01 ? 'Mutabık' : 'Mutabık değil' }}
              </v-chip>
        </td>
          </tr>
      </template>
      <template v-slot:no-data>
        <div class="text-xs-center pa-4">
            <p class="grey--text">Sipariş bazında detay bulunamadı</p>
            <p class="grey--text caption">Tarih aralığı seçip "Filtrele" butonuna basın</p>
        </div>
      </template>
    </v-data-table>
    </v-card>

    <!-- Başarı/Hata Snackbar -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="5000"
      top
      right
    >
      {{ snackbar.message }}
      <v-btn dark flat @click="snackbar.show = false">Kapat</v-btn>
    </v-snackbar>

    <!-- Transaction Type Detay Dialog -->
    <v-dialog v-model="transactionDetailDialog" max-width="1200" scrollable>
      <v-card>
        <v-card-title class="headline">
          <v-icon left>list</v-icon>
          Mutabakat Detayları - {{ selectedTransactionType !== null ? getTransactionTypeName(selectedTransactionType) : '' }}
          <v-spacer />
          <v-select
            v-model="transactionDetailFilterStatus"
            :items="modalStatusOptions"
            item-text="text"
            item-value="value"
            label="Durum Filtrele"
            style="max-width: 180px; margin-right: 12px;"
            hide-details
            dense
          />
          <v-btn icon @click="transactionDetailDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="transactionDetailHeaders"
            :items="filteredTransactionDetails"
            :loading="loadingTransactionDetails"
            :total-items="transactionDetailsTotalCount"
            :pagination.sync="transactionDetailPagination"
            :rows-per-page-items="[25, 50, 100]"
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr 
                :class="{ 'clickable-row': true }" 
                @click="showOrderBreakdown({ orderNumber: props.item.orderNumber, sku: props.item.sku })"
              >
                <td>{{ props.item.orderNumber }}</td>
                <td>{{ props.item.sku || '-' }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.expectedAmount) }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.actualAmount) }}</td>
                <td class="text-xs-right" :class="getDifferenceClass(props.item.difference)">
                  {{ formatMoney(props.item.difference) }}
                </td>
                <td class="text-xs-center">
                  <v-chip 
                    :color="getStatusColor(props.item.status)" 
                    text-color="white" 
                    small
                  >
                    {{ getStatusName(props.item.status) }}
                  </v-chip>
                </td>
                <td>{{ getOrderStatusName(props.item.orderStatus) }}</td>
              </tr>
            </template>
            <template v-slot:no-data>
              <div class="text-xs-center pa-4">
                <p class="grey--text">Detay bulunamadı</p>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" flat @click="transactionDetailDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Sipariş Detay Dialog -->
    <v-dialog v-model="orderBreakdownDialog" max-width="800">
      <v-card>
        <v-card-title class="headline">
          Sipariş Detayı: {{ selectedOrder ? selectedOrder.orderNumber : '' }}
          <v-spacer />
          <v-select
            v-model="orderBreakdownFilterStatus"
            :items="modalStatusOptions"
            item-text="text"
            item-value="value"
            label="Durum Filtrele"
            style="max-width: 180px; margin-right: 12px;"
            hide-details
            dense
          />
          <v-btn icon @click="orderBreakdownDialog = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="breakdownHeaders"
            :items="filteredOrderBreakdownItems"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td>{{ getTransactionTypeName(props.item.transactionType) }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.expectedAmount) }}</td>
                <td class="text-xs-right">{{ formatMoney(props.item.actualAmount) }}</td>
                <td class="text-xs-right" :class="getDifferenceClass(props.item.difference)">
                  {{ formatMoney(props.item.difference) }}
                </td>
                <td class="text-xs-center">
                  <v-chip 
                    :color="Math.abs(props.item.difference) < 0.01 ? 'green' : 'red'" 
                    text-color="white" 
                    small
                  >
                    {{ Math.abs(props.item.difference) < 0.01 ? 'Mutabık' : 'Mutabık değil' }}
                  </v-chip>
                </td>
              </tr>
            </template>
            <template v-slot:footer v-if="filteredOrderBreakdownItems && filteredOrderBreakdownItems.length > 0">
              <tr class="grey lighten-3 font-weight-bold">
                <td>Toplam</td>
                <td class="text-xs-right">{{ formatMoney(getFilteredBreakdownTotalExpected) }}</td>
                <td class="text-xs-right">{{ formatMoney(getFilteredBreakdownTotalActual) }}</td>
                <td class="text-xs-right" :class="getDifferenceClass(getFilteredBreakdownTotalDifference)">
                  {{ formatMoney(getFilteredBreakdownTotalDifference) }}
                </td>
                <td class="text-xs-center">
                  <v-chip 
                    :color="Math.abs(getFilteredBreakdownTotalDifference) < 0.01 ? 'green' : 'red'" 
                    text-color="white" 
                    small
                  >
                    {{ Math.abs(getFilteredBreakdownTotalDifference) < 0.01 ? 'Mutabık' : 'Mutabık değil' }}
                  </v-chip>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" flat @click="orderBreakdownDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';

export default {
  data() {
    return {
      // Filtre Menu States
      orderStartDateMenu: false,
      orderEndDateMenu: false,
      deliveredDateMenu: false,
      
      // Filtre Values
      orderStartDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      orderEndDate: moment().format('YYYY-MM-DD'),
      orderNumberFilter: null,
      orderStatus: null,
      deliveredDate: null,
      reconciliationStatus: null,
      
      // State
      selectedTenantId: null,
      selectedMpId: null,
      loadingData: false,
      recalculating: false,
      refreshingPayments: false,
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      },
      snackbarTimeout: null,
      
      // Dialog
      transactionDetailDialog: false,
      selectedTransactionType: null,
      transactionDetails: [],
      loadingTransactionDetails: false,
      transactionDetailFilterStatus: 'all',
      transactionDetailPagination: {
        page: 1,
        rowsPerPage: 50
      },
      orderBreakdownDialog: false,
      selectedOrder: null,
      orderBreakdownItems: [],
      orderBreakdownFilterStatus: 'all',
      
      // Headers
      summaryHeaders: [
        { text: 'Tip', value: 'transactionType', sortable: false },
        { text: 'Adet', value: 'totalOrders', align: 'right', sortable: true },
        { text: 'Gelen Ödeme', value: 'actualTotal', align: 'right', sortable: true },
        { text: 'Beklenen Ödeme', value: 'expectedTotal', align: 'right', sortable: true },
        { text: 'Fark', value: 'difference', align: 'right', sortable: true },
        { text: 'Durum', value: 'status', align: 'center', sortable: false }
      ],
      detailHeaders: [
        { text: 'Sipariş No', value: 'orderNumber' },
        { text: 'SKU', value: 'sku' },
        { text: 'Sipariş Tutarı', value: 'expectedTotal', align: 'right' },
        { text: 'Ödeme Tutarı', value: 'actualTotal', align: 'right' },
        { text: 'Fark', value: 'difference', align: 'right' },
        { text: 'Durum', value: 'status', align: 'center' }
      ],
      detailFilterStatus: 'all',
      detailStatusOptions: [
        { text: 'Tümü', value: 'all' },
        { text: 'Mutabık', value: 'matched' },
        { text: 'Mutabık Değil', value: 'mismatched' }
      ],
      modalStatusOptions: [
        { text: 'Tümü', value: 'all' },
        { text: 'Mutabık', value: 'matched' },
        { text: 'Mutabık Değil', value: 'mismatched' }
      ],
      loadingDetails: false,
      detailPagination: {
        page: 1,
        rowsPerPage: 25
      },
      transactionDetailHeaders: [
        { text: 'Sipariş No', value: 'orderNumber' },
        { text: 'SKU', value: 'sku' },
        { text: 'Beklenen Tutar', value: 'expectedAmount', align: 'right' },
        { text: 'Gelen Tutar', value: 'actualAmount', align: 'right' },
        { text: 'Fark', value: 'difference', align: 'right' },
        { text: 'Durum', value: 'status', align: 'center' },
        { text: 'Sipariş Durumu', value: 'orderStatus' }
      ],
      breakdownHeaders: [
        { text: 'Tip', value: 'transactionType', sortable: false },
        { text: 'Beklenen Tutar', value: 'expectedAmount', align: 'right' },
        { text: 'Gelen Tutar', value: 'actualAmount', align: 'right' },
        { text: 'Fark', value: 'difference', align: 'right' },
        { text: 'Durum', value: 'status', align: 'center' }
      ],
      
      // Options
      transactionTypeOptions: [
        { text: 'Hizmet Bedeli', value: 0 },
        { text: 'Sipariş Tutarı', value: 1 },
        { text: 'MP Stopaj', value: 2 },
        { text: 'Kargo Bedeli', value: 3 },
        { text: 'Komisyon', value: 4 },
        { text: 'İade Tutarı', value: 5 },
        { text: 'Stopaj İade', value: 6 },
        { text: 'Kampanya İndirim', value: 7 },
        { text: 'Komisyon İade', value: 8 },
        { text: 'Altın İşçilik Bedeli Stopaj', value: 9 },
        { text: 'Müşteri Memnuniyeti', value: 10 },
        { text: 'Sevkiyat Maliyeti Paylaşımı Gelir', value: 11 }
      ],
      orderStatusOptions: [
        { text: 'Tümü', value: null },
        { text: 'Normal', value: 0 },
        { text: 'İptal', value: 1 },
        { text: 'İade', value: 2 }
      ],
      reconciliationStatusOptions: [
        { text: 'Tümü', value: null },
        { text: 'Mutabık', value: 'matched' },
        { text: 'Mutabık Değil', value: 'mismatched' }
      ]
    };
  },
  computed: {
    ...mapGetters('reconciliation', ['liveSummary', 'liveOrderSummary', 'liveTransactionDetails', 'loading']),
    reconciliationSummary() {
      return this.liveSummary || [];
    },
    orderDetails() {
      return (this.liveOrderSummary && this.liveOrderSummary.items) || [];
    },
    orderDetailsTotalCount() {
      return (this.liveOrderSummary && this.liveOrderSummary.totalCount) || 0;
    },
    orderDetailsTotalPages() {
      return (this.liveOrderSummary && this.liveOrderSummary.totalPages) || 0;
    },
    transactionDetailsItems() {
      return (this.liveTransactionDetails && this.liveTransactionDetails.items) || [];
    },
    transactionDetailsTotalCount() {
      return (this.liveTransactionDetails && this.liveTransactionDetails.totalCount) || 0;
    },
    filteredOrderDetails() {
      let items = this.orderDetails;
      
      // Sipariş durumu filtresi
      if (this.orderStatus !== null && this.orderStatus !== undefined) {
        items = items.filter(item => item.orderStatus === this.orderStatus);
      }
      
      // Mutabakat durumu filtresi
      if (this.reconciliationStatus === 'matched') {
        items = items.filter(item => Math.abs(item.difference) < 0.01);
      } else if (this.reconciliationStatus === 'mismatched') {
        items = items.filter(item => Math.abs(item.difference) >= 0.01);
      }
      
      // Detay tablosu durum filtrelemesi (modal için)
      if (this.detailFilterStatus === 'matched') {
        items = items.filter(item => Math.abs(item.difference) < 0.01);
      } else if (this.detailFilterStatus === 'mismatched') {
        items = items.filter(item => Math.abs(item.difference) >= 0.01);
      }
      
      return items;
    },
    filteredTransactionDetails() {
      // Filtreleme artık backend'de yapılıyor
      return this.transactionDetailsItems;
    },
    filteredOrderBreakdownItems() {
      let items = this.orderBreakdownItems;
      
      // Order breakdown modal durum filtrelemesi
      if (this.orderBreakdownFilterStatus === 'matched') {
        items = items.filter(item => Math.abs(item.difference) < 0.01);
      } else if (this.orderBreakdownFilterStatus === 'mismatched') {
        items = items.filter(item => Math.abs(item.difference) >= 0.01);
      }
      
      return items;
    },
    getTotalOrders() {
      return this.reconciliationSummary.reduce((sum, item) => sum + (item.totalOrders || 0), 0);
    },
    getTotalExpected() {
      return this.reconciliationSummary.reduce((sum, item) => sum + (item.expectedTotal || 0), 0);
    },
    getTotalActual() {
      return this.reconciliationSummary.reduce((sum, item) => sum + (item.actualTotal || 0), 0);
    },
    getTotalDifference() {
      return this.getTotalActual - this.getTotalExpected;
    },
    getBreakdownTotalExpected() {
      return this.orderBreakdownItems.reduce((sum, item) => sum + (item.expectedAmount || 0), 0);
    },
    getBreakdownTotalActual() {
      return this.orderBreakdownItems.reduce((sum, item) => sum + (item.actualAmount || 0), 0);
    },
    getBreakdownTotalDifference() {
      return this.getBreakdownTotalActual - this.getBreakdownTotalExpected;
    },
    getFilteredBreakdownTotalExpected() {
      return this.filteredOrderBreakdownItems.reduce((sum, item) => sum + (item.expectedAmount || 0), 0);
    },
    getFilteredBreakdownTotalActual() {
      return this.filteredOrderBreakdownItems.reduce((sum, item) => sum + (item.actualAmount || 0), 0);
    },
    getFilteredBreakdownTotalDifference() {
      return this.getFilteredBreakdownTotalActual - this.getFilteredBreakdownTotalExpected;
    }
  },
  methods: {
    ...mapActions('reconciliation', [
      'fetchLiveSummary',
      'fetchLiveOrderSummary',
      'fetchOrderBreakdown',
      'fetchLiveTransactionDetails'
    ]),
    getTransactionTypeName(value) {
      const option = this.transactionTypeOptions.find(opt => opt.value === value);
      return option ? option.text : '';
    },
    formatMoney(value) {
      if (value === null || value === undefined) return '0,00';
      return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    formatDateTime(date) {
      return moment(date).format('DD.MM.YYYY HH:mm');
    },
    getDifferenceClass(difference) {
      if (Math.abs(difference) < 0.01) return 'green--text';
      return difference > 0 ? 'green--text' : 'red--text';
    },
    getReconciliationStatusColor(item) {
      if (item.errorCount > 0 || item.missingCount > 0) return 'red';
      if (Math.abs(item.difference) < 0.01) return 'green';
      return 'red';
    },
    getReconciliationStatusText(item) {
      if (Math.abs(item.difference) < 0.01 && item.errorCount === 0 && item.missingCount === 0) {
        return 'Mutabık';
      }
      return 'Mutabık değil';
    },
    applyFilter() {
      this.refresh();
    },
    clearFilters() {
      this.orderStartDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
      this.orderEndDate = moment().format('YYYY-MM-DD');
      this.orderNumberFilter = null;
      this.orderStatus = null;
      this.deliveredDate = null;
      this.deliveredDateMenu = false;
      this.reconciliationStatus = null;
      this.detailPagination.page = 1;
      this.detailPagination.rowsPerPage = 25;
      this.refresh();
    },
    async handleRecalculateAll() {
      if (!this.selectedTenantId || !this.selectedMpId) {
        this.showSnackbar('Lütfen header\'dan tenant ve pazaryeri seçin.', 'warning');
        return;
      }

      if (!this.orderStartDate || !this.orderEndDate) {
        this.showSnackbar('Lütfen tarih aralığı seçin.', 'warning');
        return;
      }

      const dateRange = `${this.orderStartDate} - ${this.orderEndDate}`;
      if (!confirm(`Tüm siparişler için beklenen ödemeler yeniden hesaplanacak.\n\nTarih Aralığı: ${dateRange}\n\nBu işlem birkaç dakika sürebilir. Devam etmek istiyor musunuz?`)) {
        return;
      }

      this.recalculating = true;
      try {
        const result = await this.$store.dispatch('reconciliation/recalculateAllExpectedPayments', {
          tenantId: this.selectedTenantId,
          mpId: this.selectedMpId,
          startDate: this.orderStartDate,
          endDate: this.orderEndDate
        });
        
        if (result && result.success) {
          this.showSnackbar(`✓ ${result.processedCount || 0} sipariş için beklenen ödemeler yeniden hesaplandı!`, 'success');
          await this.refresh();
        } else {
          this.showSnackbar('Yeniden hesaplama başlatılamadı.', 'error');
        }
      } catch (error) {
        console.error('Yeniden hesaplama hatası:', error);
        const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || 'Bilinmeyen hata';
        this.showSnackbar('Hata: ' + errorMessage, 'error');
      } finally {
        this.recalculating = false;
      }
    },
    async handleRefreshPayments() {
      if (!this.selectedTenantId || !this.selectedMpId) {
        this.showSnackbar('Lütfen header\'dan tenant ve pazaryeri seçin.', 'warning');
        return;
      }

      if (this.selectedMpId !== 1) {
        this.showSnackbar('Bu işlem sadece HepsiBurada için kullanılabilir.', 'warning');
        return;
      }

      if (!this.orderStartDate || !this.orderEndDate) {
        this.showSnackbar('Lütfen tarih aralığı seçin.', 'warning');
        return;
      }

      const dateRange = `${this.orderStartDate} - ${this.orderEndDate}`;
      if (!confirm(`Seçili tarih aralığındaki ödeme kayıtları silinip HepsiBurada API'den yeniden çekilecek.\n\nTarih Aralığı: ${dateRange}\n\nBu işlem birkaç dakika sürebilir. Devam etmek istiyor musunuz?`)) {
        return;
      }

      this.refreshingPayments = true;
      try {
        const result = await this.$store.dispatch('reconciliation/refreshPayments', {
          startDate: this.orderStartDate,
          endDate: this.orderEndDate
        });
        
        if (result && result.data && result.data.success) {
          const data = result.data;
          const message = `✓ Payment refresh tamamlandı!\n` +
            `Silinen: ${data.totalDeleted || 0} kayıt\n` +
            `Eklenen: ${data.totalInserted || 0} kayıt\n` +
            `İşlenen ay: ${data.processedMonths || 0}`;
          this.showSnackbar(message, 'success', 8000);
          // Verileri yenile
          await this.refresh();
        } else {
          this.showSnackbar('Payment refresh başlatılamadı.', 'error');
        }
      } catch (error) {
        console.error('Payment refresh hatası:', error);
        const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || 'Bilinmeyen hata';
        this.showSnackbar('Hata: ' + errorMessage, 'error');
      } finally {
        this.refreshingPayments = false;
      }
    },
    onDateChange() {
      this.orderStartDateMenu = false;
      this.orderEndDateMenu = false;
      // Tarih değiştiğinde otomatik yenile
      if (this.orderStartDate && this.orderEndDate) {
        this.refresh();
      }
    },
    onDateClear() {
      // Clear tıklandığında default tarihe dön
      this.$nextTick(() => {
        if (!this.orderStartDate) {
          this.orderStartDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        }
        if (!this.orderEndDate) {
          this.orderEndDate = moment().format('YYYY-MM-DD');
        }
        this.refresh();
      });
    },
    showSnackbar(message, color = 'success', timeout = 4000) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
      if (this.snackbarTimeout) {
        clearTimeout(this.snackbarTimeout);
      }
      this.snackbarTimeout = setTimeout(() => {
        this.snackbar.show = false;
      }, timeout);
    },
    async viewDetails(transactionType) {
      this.selectedTransactionType = transactionType;
      this.transactionDetailDialog = true;
      this.transactionDetailPagination.page = 1;
      this.transactionDetailPagination.rowsPerPage = 50;
      await this.loadTransactionDetails(transactionType);
    },
    async loadTransactionDetails(transactionType) {
      if (!transactionType && !this.selectedTransactionType) {
        return;
      }
      
      const type = transactionType || this.selectedTransactionType;
      this.loadingTransactionDetails = true;
      
      // Frontend'deki status değerini backend enum değerine çevir
      // 'all' -> null, 'matched' -> 0, 'mismatched' -> 1 (backend'de Missing+Error olarak handle ediliyor)
      let statusValue = null;
      if (this.transactionDetailFilterStatus === 'matched') {
        statusValue = 0; // Matched
      } else if (this.transactionDetailFilterStatus === 'mismatched') {
        statusValue = 1; // Mismatched (Missing + Error)
      }
      
      try {
        await this.$store.dispatch('reconciliation/fetchLiveTransactionDetails', {
          tenantId: this.selectedTenantId,
          mpId: this.selectedMpId,
          startDate: this.orderStartDate,
          endDate: this.orderEndDate,
          transactionType: type,
          page: this.transactionDetailPagination.page,
          pageSize: this.transactionDetailPagination.rowsPerPage,
          status: statusValue,
          deliveredDate: this.deliveredDate || null
        });
      } catch (error) {
        console.error('Transaction detay yükleme hatası:', error);
        this.showSnackbar('Detay yüklenirken hata oluştu.', 'error');
        this.transactionDetails = [];
      } finally {
        this.loadingTransactionDetails = false;
      }
    },
    getStatusColor(status) {
      if (status === 0) return 'green'; // Matched - Mutabık
      if (status === 1) return 'orange'; // Missing - Eksik
      return 'red'; // Error - Mutabık değil
    },
    getStatusName(status) {
      if (status === 0) return 'Mutabık';
      if (status === 1) return 'Eksik';
      return 'Mutabık değil';
    },
    getOrderStatusName(orderStatus) {
      if (orderStatus === 0) return 'Normal';
      if (orderStatus === 1) return 'İptal';
      if (orderStatus === 2) return 'İade';
      return '-';
    },
    async showOrderBreakdown(order) {
      this.selectedOrder = order;
      this.orderBreakdownDialog = true;
      
      try {
        const breakdown = await this.$store.dispatch('reconciliation/fetchOrderBreakdown', {
          tenantId: this.selectedTenantId,
          mpId: this.selectedMpId,
          orderNumber: order.orderNumber
        });
        this.orderBreakdownItems = breakdown || [];
      } catch (error) {
        console.error('Sipariş detay hatası:', error);
        this.orderBreakdownItems = [];
      }
    },
    exportToExcel() {
      // Excel export - can be implemented with a library
      this.$store.state.dialog = true;
      this.$store.state.message = 'Excel export özelliği yakında eklenecek.';
    },
    async refresh() {
      if (!this.selectedTenantId || !this.selectedMpId) {
        return;
      }
      
      if (!this.orderStartDate || !this.orderEndDate) {
        this.showSnackbar('Lütfen tarih aralığı seçin.', 'warning');
        return;
      }
      
      const params = {
        tenantId: this.selectedTenantId,
        mpId: this.selectedMpId,
        startDate: this.orderStartDate,
        endDate: this.orderEndDate,
        orderNumber: this.orderNumberFilter || null,
        orderStatus: this.orderStatus !== null && this.orderStatus !== undefined ? this.orderStatus : null,
        deliveredDate: this.deliveredDate || null
      };
      
      const detailParams = {
        ...params,
        page: this.detailPagination.page,
        pageSize: this.detailPagination.rowsPerPage
      };
      
      this.loadingData = true;
      this.loadingDetails = true;
      try {
        await Promise.all([
          this.fetchLiveSummary(params),
          this.fetchLiveOrderSummary(detailParams)
        ]);
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
        this.showSnackbar('Veri yüklenirken hata oluştu.', 'error');
      } finally {
        this.loadingData = false;
        this.loadingDetails = false;
      }
    },
    loadSelectedValues() {
      const selectedTenant = localStorage.getItem('selectedTenantId');
      const selectedMp = localStorage.getItem('selectedMpId');
      const newTenantId = selectedTenant ? parseInt(selectedTenant) : null;
      const newMpId = selectedMp ? parseInt(selectedMp) : null;
      
      if (this.selectedTenantId !== newTenantId || this.selectedMpId !== newMpId) {
        this.selectedTenantId = newTenantId;
        this.selectedMpId = newMpId;
        if (this.selectedTenantId && this.selectedMpId) {
          this.refresh();
        }
      }
    },
    handleStorageChange(event) {
      if (event.key === 'selectedTenantId' || event.key === 'selectedMpId') {
        this.loadSelectedValues();
      }
    }
  },
  watch: {
    '$store.state': {
      handler() {
        this.loadSelectedValues();
      },
      deep: true
    },
    detailPagination: {
      handler() {
        this.refresh();
      },
      deep: true
    },
    transactionDetailPagination: {
      handler() {
        if (this.transactionDetailDialog && this.selectedTransactionType !== null) {
          this.loadTransactionDetails();
        }
      },
      deep: true
    },
    transactionDetailFilterStatus() {
      // Filtre değiştiğinde pagination'ı sıfırla ve yeniden yükle
      if (this.transactionDetailDialog && this.selectedTransactionType !== null) {
        this.transactionDetailPagination.page = 1;
        this.loadTransactionDetails();
      }
    }
  },
  async mounted() {
    this.loadSelectedValues();
    window.addEventListener('storage', this.handleStorageChange);
    this.storageCheckInterval = setInterval(() => {
      this.loadSelectedValues();
    }, 500);
    await this.refresh();
  },
  beforeDestroy() {
    if (this.storageCheckInterval) {
      clearInterval(this.storageCheckInterval);
    }
    window.removeEventListener('storage', this.handleStorageChange);
  }
};
</script>

<style scoped>
.clickable-row {
  cursor: pointer;
}
.clickable-row:hover {
  background-color: #f5f5f5;
}
</style>
